import { Document } from "./Document"
import { SelectionManager } from "./SelectionManager"
import { ToolManager } from "./ToolManager"
import { EditorState, ToolOptions } from "./EditorState"
import { Tool } from "./tools/Tool"
import { RenderPort } from "./ports/RenderPort"
import type { PointerEventData } from "./types/InputTypes"
import { GroupService } from "./services/GroupService"
import { ZOrderService } from "./services/ZOrderService"
import { SpatialIndexService } from "./services/SpatialIndexService"
import { ShapeQueryService } from "./services/ShapeQueryService"
import { EventBus } from "./EventBus"
import { CommandManager } from "./commands/CommandManager"
import {
  SetToolCommand,
  UpdateToolOptionsCommand,
  ClearCommand,
} from "./commands"
import { InputManager } from "./InputManager"

export class Editor {
  readonly document = new Document()
  readonly events = new EventBus()
  readonly selection = new SelectionManager(this.events)
  readonly state = new EditorState(this.events)
  readonly tools = new ToolManager(this)
  readonly commands: CommandManager
  readonly input: InputManager
  renderer?: RenderPort

  readonly zOrder: ZOrderService
  readonly groupService: GroupService
  readonly spatialIndex: SpatialIndexService
  readonly shapeQuery: ShapeQueryService

  constructor() {
    this.commands = new CommandManager(this.events)
    this.input = new InputManager(this)
    this.zOrder = new ZOrderService(this.document)
    this.groupService = new GroupService(this.document, this.zOrder)
    this.spatialIndex = new SpatialIndexService(this.document, this.events)
    this.shapeQuery = new ShapeQueryService(this.document, this.spatialIndex)
  }

  /**
   * Subscribe to editor events
   * @param event - Event name
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  on(event: string, callback: (data?: unknown) => void): () => void {
    return this.events.on(event, callback)
  }

  /**
   * Emit an event (for internal use)
   * @param event - Event name
   * @param data - Event data
   */
  private emit(event: string, data?: unknown): void {
    this.events.emit(event, data)
  }

  addTools(tools: Tool[]) {
    this.tools.addTools(tools)
  }

  setActiveTool(tool: string) {
    this.commands.execute(new SetToolCommand(this, tool))
  }

  updateToolOptions(options: Partial<ToolOptions>) {
    this.commands.execute(new UpdateToolOptionsCommand(this, options))
  }

  getToolOption(key: keyof ToolOptions) {
    return this.state.getToolOption(key)
  }

  onPointerDown(e: PointerEventData) {
    this.input.handlePointerDown(e)
  }

  onPointerMove(e: PointerEventData) {
    this.input.handlePointerMove(e)
  }

  onPointerUp(e: PointerEventData) {
    this.input.handlePointerUp(e)
  }

  onKeyDown(e: KeyboardEvent) {
    this.input.handleKeyDown(e)
  }

  onKeyUp(e: KeyboardEvent) {
    this.input.handleKeyUp(e)
  }

  setRenderer(renderer: RenderPort) {
    this.renderer = renderer
  }

  clear() {
    this.commands.execute(new ClearCommand(this))
  }
}
